var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { PeerRemovedMessage } from "../../messages/session/PeerMessages";
import { busService } from "../bus";
import { CopyMediaFileToClipboardMessage, DownloadMediaFileMessage, DownloadTextFileMessage, ShareMediaFileMessage, StopShareMediaFileMessage } from "../../messages/media";
import { AskErrorMessage, AskOkMessage } from "flavr-common";
import { downloadService } from "./DownloadService";
import { peerFileSystemService } from "../multiplayer/peer";
import { fileService } from "../files";
import { AddFileDataMessage, AddFileDataResponseMessage } from "../../messages/file/FileMessages";
class MediaFileService {
    constructor() {
        this.mediaFiles = new Map();
        this.peerMediaFiles = new Map();
        this.connection = busService.connection("MediaFileService");
        this.handleAddFileDataMessage = (msg, askId) => {
            const blob = new Blob([msg.data], { type: "octet/stream" });
            const fileId = this.addMediaFile(msg.fileName, {
                file: blob,
                type: msg.fileTypeExtension
            });
            if (askId !== undefined) {
                this.connection.answer(askId, new AddFileDataResponseMessage(fileId));
            }
        };
        this.handlePeerRemovedMessage = (msg) => {
            this.peerMediaFiles.delete(msg.clientId);
        };
        this.handleDownloadMediaFileMessage = (msg, askId) => __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.download(msg.fileId, msg.fileName);
                if (askId !== undefined) {
                    this.connection.answer(askId, new AskOkMessage());
                }
            }
            catch (error) {
                if (askId !== undefined) {
                    this.connection.answer(askId, new AskErrorMessage(JSON.stringify(error)));
                }
            }
        });
        this.handleDownloadTextFileMessage = (msg, askId) => {
            try {
                downloadService.downloadText(msg.fileName, msg.text);
                if (askId !== undefined) {
                    this.connection.answer(askId, new AskOkMessage());
                }
            }
            catch (error) {
                if (askId !== undefined) {
                    this.connection.answer(askId, new AskErrorMessage(JSON.stringify(error)));
                }
            }
        };
        this.handleCopyMediaFileToClipboardMessage = (msg, askId) => __awaiter(this, void 0, void 0, function* () {
            const result = yield this.copyToClipboard(msg.fileId);
            if (askId !== undefined) {
                this.connection.answer(askId, result ? new AskOkMessage() : new AskErrorMessage());
            }
        });
        this.handleShareMediaFileMessage = (msg, askId) => __awaiter(this, void 0, void 0, function* () {
            try {
                const mediaData = this.mediaFiles.get(msg.fileId);
                if (!mediaData) {
                    throw new Error(`Can't find file for fileId ${msg.fileId}`);
                }
                if (!mediaData.file) {
                    throw new Error(`No file defined for fileId ${msg.fileId}`);
                }
                const peerFile = peerFileSystemService.createPeerFile(`${msg.fileId}`);
                mediaData.peerFile = peerFile;
                peerFile.fileTypeExtension = mediaData.fileTypeExtension;
                peerFile.setData(mediaData.file, mediaData.url);
                yield peerFile.send();
                if (askId !== undefined) {
                    this.connection.answer(askId, new AskOkMessage());
                }
            }
            catch (error) {
                console.error(error);
                if (askId !== undefined) {
                    this.connection.answer(askId, new AskErrorMessage(JSON.stringify(error)));
                }
            }
        });
        this.handleStopShareMediaFileMessage = (msg, askId) => {
            const mediaData = this.mediaFiles.get(msg.fileId);
            if (!mediaData) {
                throw new Error(`Can't find file for fileId ${msg.fileId}`);
            }
            if (mediaData.peerFile) {
                mediaData.peerFile.close();
            }
            this.mediaFiles.delete(msg.fileId);
            if (askId !== undefined) {
                this.connection.answer(askId, new AskOkMessage());
            }
        };
        this.connection.subscribeByType(PeerRemovedMessage, this.handlePeerRemovedMessage);
        this.connection.subscribeByTypeOrAskRequest(DownloadMediaFileMessage, this.handleDownloadMediaFileMessage);
        this.connection.subscribeByTypeOrAskRequest(DownloadTextFileMessage, this.handleDownloadTextFileMessage);
        this.connection.subscribeByTypeOrAskRequest(CopyMediaFileToClipboardMessage, this.handleCopyMediaFileToClipboardMessage);
        this.connection.subscribeByTypeOrAskRequest(ShareMediaFileMessage, this.handleShareMediaFileMessage);
        this.connection.subscribeByTypeOrAskRequest(StopShareMediaFileMessage, this.handleStopShareMediaFileMessage);
        this.connection.subscribeByTypeOrAskRequest(AddFileDataMessage, this.handleAddFileDataMessage);
    }
    addMediaFile(url, file) {
        const fileId = fileService.generateUUIDFileId();
        console.log("addMediaFile", url, fileId);
        this.mediaFiles.set(fileId, {
            url,
            file: file === null || file === void 0 ? void 0 : file.file,
            fileTypeExtension: file === null || file === void 0 ? void 0 : file.type
        });
        return fileId;
    }
    download(fileId, fileName) {
        return __awaiter(this, void 0, void 0, function* () {
            let url;
            const mediaFile = this.mediaFiles.get(fileId);
            if (mediaFile) {
                url = mediaFile.url;
            }
            else {
                const file = peerFileSystemService.getFile(`${fileId}`);
                if (!file) {
                    throw new Error(`Can't find file for fileId ${fileId}`);
                }
                url = yield file.getOrCreateUrl();
            }
            if (!url) {
                throw new Error(`Can't generate url for fileId ${fileId}`);
            }
            downloadService.downloadFile(fileName, url);
        });
    }
    copyToClipboard(fileId) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                fileId = Number(fileId);
                let data;
                const mediaFile = this.mediaFiles.get(fileId);
                if (mediaFile) {
                    data = mediaFile.file;
                }
                if (!data) {
                    const file = peerFileSystemService.getFile(`${fileId}`);
                    if (!file) {
                        throw new Error(`Can't find file for fileId ${fileId}`);
                    }
                    data = yield file.getData();
                }
                if (!data)
                    return false;
                const clipboard = navigator.clipboard;
                if (typeof data === "string") {
                    yield clipboard.writeText(data);
                }
                else {
                    const item = [new ClipboardItem({ [data.type]: data })];
                    yield clipboard.write(item);
                }
            }
            catch (error) {
                console.log(error);
                return false;
            }
            return true;
        });
    }
}
export const mediaFileService = new MediaFileService();
